import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { get, post, put, deleteRequestV2 } from '../../api/usersModule/httpClient';
import { useDebounce } from '../../hooks/useDebounce';
import { LargeCard } from '../Cards/LargeCard';
import { CDBModal } from "cdbreact";
import { ModalCategories } from "../Modals/ModalCategories";

const usersHeaders = ['ID', 'Nombre', "/categories"];

const defaultFormData = {
  name: ''
};

export const CrudCategories = ({ userName, permissions, project }) => {
  const [data, setData] = useState([]);
  const [state, setState] = useState({ modal: false });
  const [page, setPage] = useState(1);
  const [pressedButton, setPressedButton] = useState('');
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [totalItems, setTotalItems] = useState(0);
  const [initialRender, setInitialRender] = useState(true);
  const [changePage, setChangePage] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    ...defaultFormData,
    user: userName,
  });

  const fetchCategories = async (search, statusPage) => {
    setLoading(true);
    let response;
    try {
      if (search !== '') {
        response = await get(`/categories?page=${statusPage}&search=${search}&limit=${itemsPerPage}`);
      } else {
        response = await get(`/categories?page=${statusPage}&limit=${itemsPerPage}`);
      }
      setTotalItems(response.answer ? response.pagination.totalItems : 0);
      
      // Sanitizar y convertir datos al formato que espera la tabla
      const sanitizedData = response.answer && response.data ? 
        response.data.map(item => [
          String(item.id || ''),
          String(item.name || '')
        ]) : [];
      
      setData(sanitizedData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setData([]);
      setTotalItems(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!initialRender || changePage) {
      fetchCategories(debouncedSearch, page);
      setChangePage(false);
    }
  }, [debouncedSearch, page, changePage, itemsPerPage]);

  useEffect(() => {
    if (initialRender) {
      fetchCategories('', 1);
      setInitialRender(false);
    }
  }, [initialRender]);

  useEffect(() => {
    switch (pressedButton.split(',')[0]) {
      case "create":
        openCreateModal();
        break;
      case "edit":
        // El ID viene en pressedButton como "edit,ID"
        const categoryId = pressedButton.split(',')[1];
        const categoryArray = data.find(item => item[0] == categoryId);
        if (categoryArray) {
          // Convertir array a objeto para el modal
          const category = {
            id: categoryArray[0],
            name: categoryArray[1]
          };
          openEditModal(category);
        }
        break;
      case "refresh":
        fetchCategories(debouncedSearch, page);
        break;
      case "closeModal":
        setState({ modal: false });
        break;
      case "submitModal":
        handleSubmit();
        break;
      case "delete":
        if (formData.id) {
          handleDelete(formData.id);
        }
        break;
      default:
        break;
    }
    setPressedButton("");
  }, [pressedButton, debouncedSearch, page, data]);

  const handleCreate = async () => {
    Swal.fire({
      title: 'Creando Categoría',
      text: 'Por favor, espere...',
      icon: 'info',
      allowOutsideClick: false,
      showCancelButton: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await post('/categories', formData);
      Swal.close();
      
      if (response.answer) {
        fetchCategories(debouncedSearch, page);
        setState({ modal: false });
        setFormData({ ...defaultFormData, user: userName });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error al crear la categoría',
          text: response.message || 'Error desconocido'
        });
      }
    } catch (error) {
      Swal.close();
      console.error('Error creating category:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al crear la categoría',
        text: 'Error de conexión'
      });
    }
  };

  const handleUpdate = async () => {
    Swal.fire({
      title: 'Actualizando Categoría',
      text: 'Por favor, espere...',
      icon: 'info',
      allowOutsideClick: false,
      showCancelButton: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await put(`/categories`, formData);
      Swal.close();
      
      if (response.answer) {
        fetchCategories(debouncedSearch, page);
        setState({ modal: false });
        setFormData({ ...defaultFormData, user: userName });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error al actualizar la categoría',
          text: response.message || 'Error desconocido'
        });
      }
    } catch (error) {
      Swal.close();
      console.error('Error updating category:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al actualizar la categoría',
        text: 'Error de conexión'
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: '¿Estás seguro?',
        text: 'No podrás revertir esta acción',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        Swal.fire({
          title: 'Eliminando Categoría',
          text: 'Por favor, espere...',
          icon: 'info',
          allowOutsideClick: false,
          showCancelButton: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await deleteRequestV2(`/categories`, JSON.stringify({ id }));
        Swal.close();
        
        if (response.answer) {
          Swal.fire({
            icon: 'success',
            title: 'Categoría eliminada exitosamente',
            showConfirmButton: false,
            timer: 1500
          });
          fetchCategories(debouncedSearch, page);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error al eliminar la categoría',
            text: response.message || 'Error desconocido'
          });
        }
      }
    } catch (error) {
      Swal.close();
      console.error('Error deleting category:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al eliminar la categoría',
        text: 'Error de conexión'
      });
    }
  };

  const openCreateModal = () => {
    setAction('create');
    setFormData({ ...defaultFormData, user: userName });
    setState({ modal: true });
  };

  const openEditModal = (category) => {
    setAction('edit');
    setFormData({ ...category, user: userName });
    setState({ modal: true });
  };

  const handleSubmit = () => {
    if (action === 'create') {
      handleCreate();
    } else if (action === 'edit') {
      handleUpdate();
    }
  };

  return (
    <div>
      <LargeCard
        name="Gestión de Categorías"
        headers={usersHeaders}
        data={data}
        loading={loading}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        setChangePage={setChangePage}
        setPressedButton={setPressedButton}
        cState={true}
      />
      
      <CDBModal
        isOpen={state.modal}
        toggle={() => setState({ modal: !state.modal })}
        size="lg"
      >
        <ModalCategories
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={() => setState({ modal: false })}
          action={action}
          setPressedButton={setPressedButton}
        />
      </CDBModal>
    </div>
  );
};